import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAppRuntime } from "../../providers/AppRuntimeProvider";
import { useRpcMutation } from "../../runtime/hooks/useRpc";
import type { EpisodeDetailView, EpisodeListItem } from "./types";
import type { PaginatedResponse } from "../../hooks/types";

export const episodeKeys = {
  all: ["episodes"] as const,
  list: (params?: Record<string, unknown>) => [...episodeKeys.all, "list", params] as const,
  detail: (id: string) => [...episodeKeys.all, "detail", id] as const,
};

async function fetchAllEpisodes(
  rpc: <T>(method: string, params: Record<string, unknown>) => Promise<T>,
): Promise<readonly EpisodeListItem[]> {
  const pageSize = 200;
  let offset = 0;
  const collected: EpisodeListItem[] = [];
  const seenIds = new Set<string>();

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  while (true) {
    const page = await rpc<PaginatedResponse<EpisodeListItem>>(
      "episodes.list",
      { limit: pageSize, offset },
    );
    if (page.items.length === 0) break;

    for (const item of page.items) {
      if (seenIds.has(item.id)) continue;
      seenIds.add(item.id);
      collected.push(item);
    }

    offset += page.items.length;
    if (collected.length >= page.total) break;
  }

  return collected;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/explicit-module-boundary-types
export function useEpisodeDetailQuery(episodeId: string | undefined) {
  const runtime = useAppRuntime();
  return useQuery({
    queryKey: episodeKeys.detail(episodeId ?? ""),
    queryFn: () =>
      runtime.transport.rpc<EpisodeDetailView>("episodes.get", { id: episodeId }),
    enabled: !!episodeId,
    staleTime: 30_000,
  });
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/explicit-module-boundary-types
export function useEpisodesListQuery(options?: { readonly enabled?: boolean }) {
  const runtime = useAppRuntime();
  return useQuery({
    queryKey: episodeKeys.list(),
    queryFn: () => fetchAllEpisodes(
      (method, params) => runtime.transport.rpc(method, params),
    ),
    staleTime: 30_000,
    enabled: options?.enabled ?? true,
  });
}

// ─── Optimistic create (same pattern as useModuleCreate in notes/projects) ──

interface CreateEpisodeParams {
  readonly title: string;
  readonly client_id: string;
  readonly mentioned_entity_ids?: readonly string[];
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/explicit-module-boundary-types
export function useCreateEpisodeMutation() {
  const runtime = useAppRuntime();
  const queryClient = useQueryClient();

  return useMutation<
    { id: string },
    Error,
    CreateEpisodeParams,
    { previous: readonly EpisodeListItem[] | undefined }
  >({
    mutationFn: (params) =>
      runtime.transport.rpc<{ id: string }>("episodes.create", { ...params }),
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: episodeKeys.all });
      const previous = queryClient.getQueryData<readonly EpisodeListItem[]>(episodeKeys.list());
      const now = new Date().toISOString();
      const optimistic: EpisodeListItem = {
        id: variables.client_id,
        title: variables.title,
        status: "active",
        is_archived: false,
        message_count: 0,
        created_at: now,
        updated_at: now,
        date: now,
      };
      queryClient.setQueryData<readonly EpisodeListItem[]>(
        episodeKeys.list(),
        (old) => [optimistic, ...(old ?? [])],
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(episodeKeys.list(), context.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: episodeKeys.all });
    },
  });
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/explicit-module-boundary-types
export function useArchiveEpisodeMutation() {
  const queryClient = useQueryClient();
  return useRpcMutation<{ status: string }, { id: string }>("episodes.archive", {
    onSuccess: () => queryClient.invalidateQueries({ queryKey: episodeKeys.all }),
  });
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/explicit-module-boundary-types
export function useUnarchiveEpisodeMutation() {
  const queryClient = useQueryClient();
  return useRpcMutation<{ status: string }, { id: string }>("episodes.unarchive", {
    onSuccess: () => queryClient.invalidateQueries({ queryKey: episodeKeys.all }),
  });
}
